import { useWatch } from 'react-hook-form';
import type { ClientGroup } from '@/views/Buyers/types';
import { isAgreementExpired } from '@/views/Buyers/utils/isAgreementExpired';
import { BuyerMethod, type CreateOfferFormData } from '../types';

type BuyerAgreementStatusResponse = ClientGroup['agreementStatus'];
// Map null to a string value to allow for the AGREEMENT_STATUS_MAP to be used
type NonNullableBuyerAgreementStatusResponse =
  | NonNullable<BuyerAgreementStatusResponse>
  | 'none';

export enum BuyerAgreementStatus {
  EXPIRED = 'expired',
  SIGNED = 'signed',
  PARTIALLY_SIGNED = 'partiallySigned',
  NOT_SIGNED = 'notSigned',
  NOT_SENT = 'notSent',
}

const AGREEMENT_STATUS_MAP: Record<
  NonNullableBuyerAgreementStatusResponse,
  BuyerAgreementStatus
> = {
  completed: BuyerAgreementStatus.SIGNED,
  partiallySigned: BuyerAgreementStatus.PARTIALLY_SIGNED,
  notFinalized: BuyerAgreementStatus.NOT_SIGNED,
  created: BuyerAgreementStatus.NOT_SIGNED,
  delivered: BuyerAgreementStatus.NOT_SIGNED,
  sending: BuyerAgreementStatus.NOT_SIGNED,
  sent: BuyerAgreementStatus.NOT_SIGNED,
  declined: BuyerAgreementStatus.NOT_SENT,
  failed: BuyerAgreementStatus.NOT_SENT,
  voided: BuyerAgreementStatus.NOT_SENT,
  none: BuyerAgreementStatus.NOT_SENT,
};

/**
 * Gets the buyer agreement status for a given buyer and reference date.
 * @param buyer - The buyer to get the agreement status for
 * @param referenceDate - The reference date to use to check if the agreement is expired
 * @returns The buyer agreement status
 */
export function getBuyerAgreementStatus(
  buyer?: Pick<ClientGroup, 'agreementStatus' | 'endDate'>,
  referenceDate?: Date,
): BuyerAgreementStatus {
  // Check if agreement is expired first - this overrides all other statuses
  if (buyer?.endDate && isAgreementExpired(buyer.endDate, referenceDate)) {
    return BuyerAgreementStatus.EXPIRED;
  }

  const status: NonNullableBuyerAgreementStatusResponse =
    buyer?.agreementStatus ?? 'none';

  return AGREEMENT_STATUS_MAP[status];
}

/**
 * Maps the buyer agreement status to the status categories pertinant to the application logic
 */
export const useBuyerAgreementStatus = (): BuyerAgreementStatus => {
  const [buyer, buyerMethod] = useWatch<
    CreateOfferFormData,
    ['buyer', 'buyerMethod']
  >({ name: ['buyer', 'buyerMethod'] });

  if (!buyer || buyerMethod === BuyerMethod.newBuyer) {
    return BuyerAgreementStatus.NOT_SENT;
  }

  return getBuyerAgreementStatus(buyer);
};
